/*
  Ever Leaf <-> Mini Final-Submit Upload Adapter (Phase 4, "Real Shopify
  Line Item Properties" correction).

  ARCHITECTURE (preserved, do not revisit): Mini/FilePond has no role in
  Upload/Change/Remove/drag/arrows/zoom/pinch/Reset/Text editing at all. All
  of that is handled entirely by Ever Leaf's own hidden native file input +
  PersonalizerState + Canvas renderer (see assets/ever-leaf-personalizer.js).
  Mini is invoked exactly once, at the moment the customer commits via Add to
  Cart or Buy Now.

  Pass 4 gave us: pond.addFile() + pond.processFile() reliably drives a
  FilePond item to FileStatus.PROCESSING_COMPLETE (STAGE A below, unchanged).
  Pass 5's side-by-side Dev evidence proved `item.serverId` is a real hosted
  Mini URL the moment PROCESSING_COMPLETE is reached. Both remain true and
  unchanged — this file's ONLY job, per this correction's Section 2, is:

    current customerFile -> upload/storage -> PROCESSING_COMPLETE -> item.serverId

  ARCHITECTURAL CORRECTION (this pass): Pass 6/7 iterated on WHICH party
  (Ever Leaf vs. Mini) should own the public `properties[Upload 1]` line-item
  property, based on DOM/cart-drawer evidence that turned out to be
  misleading. The actual authoritative /cart.js response, after a real Add
  to Cart, showed `"properties": {}` — NONE of the properties (not Mini's
  hypothesized one, not Ever Leaf's own five, not even the always-non-empty
  `_ever_leaf_config_version`) actually reached Shopify. That rules out
  "Mini has its own reliable submit-time hook" as the explanation, and
  points instead to a form/submission-path bug (see
  assets/ever-leaf-personalizer.js's canonicalizeLineItemProperties() and
  its Section 5 form-resolution fix for the actual correction). Mini/
  FilePond is therefore now DEFINITIVELY storage-only — it must never create
  or touch ANY `properties[...]` input, including the public one. Ever Leaf
  owns and creates ALL SEVEN line-item properties itself, canonicalized
  immediately before every commit's approved resubmit. `createOwnUploadProperty()`
  below is kept only as inert legacy code from that iteration; it is not
  called by finalizeUpload and should be considered dead.

  FileStatus numeric values (confirmed against FilePond's own public
  constants, matches the brief's table exactly):
    INIT=1, IDLE=2, PROCESSING=3, LOADING=7, LOAD_ERROR=8,
    PROCESSING_QUEUED=9, PROCESSING_COMPLETE=5, PROCESSING_ERROR=6,
    PROCESSING_REVERT_ERROR=10.
  Falls back to these hardcoded values if window.FilePond.FileStatus isn't
  exposed for some reason — they're FilePond's own long-stable public API,
  not an app-specific guess.
*/
(function () {
  'use strict';

  if (window.EverLeafMiniUploadAdapter) return;

  var UPLOAD_PROPERTY_SELECTOR = 'input[name^="properties[Upload"]';
  // The property name createOwnUploadProperty() would use if ever
  // re-enabled (Correction Pass 7, Section 8 fallback) — NOT used by the
  // main flow as of Pass 7; Mini's own submit-time hook is now the sole
  // source of the public Upload property.
  var UPLOAD_PROPERTY_NAME = 'Upload 1';
  // Marks a fallback-created input as Ever-Leaf-owned, so any future
  // diagnostic can distinguish it from a Mini-created one (Correction Pass
  // 7, Section 4) — set by createOwnUploadProperty() if that fallback is
  // ever re-enabled.
  var OWNED_PROPERTY_MARKER = 'data-ever-leaf-owned-upload-property';
  var STAGE_A_POLL_INTERVAL_MS = 250;
  var STAGE_A_TIMEOUT_MS = 25000; // Stage A (FilePond processing) timeout, separate from Stage B's

  var FILE_STATUS_FALLBACK = {
    INIT: 1,
    IDLE: 2,
    PROCESSING: 3,
    PROCESSING_COMPLETE: 5,
    PROCESSING_ERROR: 6,
    LOADING: 7,
    LOAD_ERROR: 8,
    PROCESSING_QUEUED: 9,
    PROCESSING_REVERT_ERROR: 10
  };
  var FILE_STATUS_NAMES = {
    1: 'INIT',
    2: 'IDLE',
    3: 'PROCESSING',
    5: 'PROCESSING_COMPLETE',
    6: 'PROCESSING_ERROR',
    7: 'LOADING',
    8: 'LOAD_ERROR',
    9: 'PROCESSING_QUEUED',
    10: 'PROCESSING_REVERT_ERROR'
  };
  var POND_STATUS_FALLBACK = { EMPTY: 0, IDLE: 1, ERROR: 2, BUSY: 3, READY: 4 };
  var POND_STATUS_NAMES = { 0: 'EMPTY', 1: 'IDLE', 2: 'ERROR', 3: 'BUSY', 4: 'READY' };

  function fileStatusName(code) {
    return FILE_STATUS_NAMES[code] || 'UNKNOWN(' + code + ')';
  }
  function pondStatusName(code) {
    return POND_STATUS_NAMES[code] || 'UNKNOWN(' + code + ')';
  }

  function findFilePondRoot() {
    return document.querySelector('.filepond--root');
  }

  function resolvePondInstance() {
    var root = findFilePondRoot();
    if (!root || !window.FilePond || typeof window.FilePond.find !== 'function') return null;
    // Never cache this — FilePond instances may be recreated (Section 4).
    return window.FilePond.find(root);
  }

  /*
    Returns every CURRENTLY populated (non-empty .value) Mini upload property
    input within `scope` — the exact, name-agnostic source of truth. An
    input matching the selector but with an empty value is treated as
    not-yet-ready / not a real hosted property.
  */
  function findPopulatedUploadProperties(scope) {
    var all = (scope || document).querySelectorAll(UPLOAD_PROPERTY_SELECTOR);
    return Array.prototype.filter.call(all, function (input) {
      return !!input.value;
    });
  }

  // ALL matching inputs regardless of value — used to strip stale state
  // completely (Mini's own leftovers, ours from a prior attempt, empty
  // placeholders, anything), never leaving ambiguous partial state behind.
  function removeAllUploadPropertyInputs(scope) {
    var all = (scope || document).querySelectorAll(UPLOAD_PROPERTY_SELECTOR);
    all.forEach(function (input) {
      input.parentNode && input.parentNode.removeChild(input);
    });
  }

  /*
    Removes stale Mini-owned hidden property inputs from a prior
    interrupted/replaced upload, and clears any files FilePond itself still
    thinks are active — so an upload we're about to start can never leave a
    duplicate/old link behind. Safe to call even if nothing is stale.
  */
  function sanitizeStaleState(scope, onStatus) {
    onStatus = onStatus || function () {};
    var pond = resolvePondInstance();
    onStatus('clearing-start');
    if (pond && typeof pond.removeFiles === 'function') {
      try {
        pond.removeFiles();
      } catch (e) {
        onStatus('clearing-error');
      }
    }
    removeAllUploadPropertyInputs(scope);
    onStatus('clearing-done');
  }

  /*
    Correction Pass 7, Section 8 FALLBACK ONLY — NOT called by the main flow
    as of this pass. Creates the ONE authoritative hidden
    `properties[Upload 1]` input on the real product form ourselves, marked
    with OWNED_PROPERTY_MARKER, using the confirmed-valid `item.serverId` as
    its value. Any pre-existing matching input(s) are stripped first so
    exactly one survives. Kept and exported only in case live testing ever
    proves Mini does NOT reliably create its own public property after all
    (in which case this should be called again from finalizeUpload's
    onStageAComplete, exactly as Pass 6 did).
  */
  function createOwnUploadProperty(scope, hostedUrl) {
    removeAllUploadPropertyInputs(scope);
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + UPLOAD_PROPERTY_NAME + ']';
    input.value = hostedUrl;
    input.setAttribute(OWNED_PROPERTY_MARKER, 'true');
    (scope || document).appendChild(input);
    return input;
  }

  /*
    Section 6 — best-effort, read-only inspection of the CURRENT Pond's
    configuration, purely for DEV diagnosis. Never mutates Mini's config.
    FilePond exposes options as direct getters on the pond instance (e.g.
    pond.instantUpload, pond.allowProcess, pond.maxParallelUploads,
    pond.server) — each read defensively since the exact getter surface
    isn't independently verifiable from this environment (Mini's own
    integration lives in an app-injected block, invisible to this repo).
  */
  function inspectPondConfig(pond) {
    function safe(fn) {
      try {
        return fn();
      } catch (e) {
        return undefined;
      }
    }
    var server = safe(function () {
      return pond.server;
    });
    var hasServerProcess = !!(server && (typeof server === 'function' || server.process));
    return {
      instantUpload: safe(function () {
        return pond.instantUpload;
      }),
      allowProcess: safe(function () {
        return pond.allowProcess;
      }),
      maxParallelUploads: safe(function () {
        return pond.maxParallelUploads;
      }),
      hasServerProcess: hasServerProcess
    };
  }

  /*
    STAGE A (Correction Pass 4): drives a single FilePond item from whatever
    status addFile() left it in through to PROCESSING_COMPLETE, calling
    pond.processFile(item.id) EXACTLY ONCE whenever the item is IDLE or
    PROCESSING_QUEUED (Sections 4/5 — queued is NOT processing; the previous
    pass only handled IDLE, which is why a queued item could sit forever).
    Reports every status transition via onStatus('file-status', code) and
    the processFile call's own outcome via onStatus('processfile-called'/
    'processfile-resolved'/'processfile-error', detail) — Section 7.
  */
  function driveFileProcessing(pond, item, onStatus, onComplete, onError) {
    var FileStatus = (window.FilePond && window.FilePond.FileStatus) || FILE_STATUS_FALLBACK;
    var settled = false;
    var processFileCalled = false;
    var pollTimer = null;
    var start = Date.now();
    var lastReportedStatus = null;

    function ensureProcessing() {
      if (processFileCalled) return; // never process the same item twice (Section 5)
      if (typeof pond.processFile !== 'function') {
        onStatus('processfile-error', 'pond.processFile is not a function — cannot start processing programmatically');
        return;
      }
      processFileCalled = true;
      onStatus('processfile-called');
      try {
        var result = pond.processFile(item.id);
        if (result && typeof result.then === 'function') {
          result
            .then(function () {
              onStatus('processfile-resolved');
            })
            .catch(function (err) {
              onStatus('processfile-error', (err && err.message) || 'processFile rejected');
            });
        } else {
          // Some FilePond versions' processFile isn't promise-returning —
          // the item's own .status is still the authoritative signal below.
          onStatus('processfile-resolved');
        }
      } catch (e) {
        onStatus('processfile-error', (e && e.message) || 'processFile threw');
      }
    }

    function check() {
      if (settled) return;
      var code = item.status;
      if (code !== lastReportedStatus) {
        lastReportedStatus = code;
        onStatus('file-status', code);
      }

      if (code === FileStatus.PROCESSING_COMPLETE) {
        settled = true;
        cleanup();
        onComplete();
        return;
      }
      if (
        code === FileStatus.PROCESSING_ERROR ||
        code === FileStatus.LOAD_ERROR ||
        code === FileStatus.PROCESSING_REVERT_ERROR
      ) {
        settled = true;
        cleanup();
        onError('FilePond processing failed (status ' + fileStatusName(code) + ').');
        return;
      }
      if (code === FileStatus.IDLE || code === FileStatus.PROCESSING_QUEUED) {
        // Explicitly ensure/start processing for BOTH — queued alone does
        // not mean this Pond's configuration will ever actually start
        // uploading it on its own (Section 4).
        ensureProcessing();
      }
      // PROCESSING / LOADING / INIT: already in flight — just keep waiting.

      if (Date.now() - start > STAGE_A_TIMEOUT_MS) {
        settled = true;
        cleanup();
        onError('FilePond did not finish processing within the expected time (stuck at ' + fileStatusName(code) + ').');
      }
    }

    function cleanup() {
      if (pollTimer) clearInterval(pollTimer);
    }

    check(); // immediate synchronous check — Stage A may already be complete
    if (!settled) pollTimer = setInterval(check, STAGE_A_POLL_INTERVAL_MS);

    return cleanup;
  }

  /*
    The one and only Mini touchpoint left in the whole app. Takes the CURRENT
    authoritative File, sanitizes any stale prior Mini state, injects the
    file via FilePond's public addFile() API, drives it through Stage A
    (real FilePond processing completion), then — Correction Pass 7 — reads
    the item's own `serverId` as the authoritative hosted URL and resolves.
    Does NOT create any public `properties[Upload*]` input (Section 5/6 —
    Mini's own submit-time hook is the sole source of that now); the caller
    (ever-leaf-personalizer.js) writes serverId into the PRIVATE
    `properties[_ever_leaf_upload_url]` property instead. Resolves the
    FilePond instance fresh at the moment of use — never cached.

    `scopeEl` (optional, defaults to `document`) should be the real product
    form.

    handlers.onReady(result) — result = { hostedUrl, itemId, filename }.
    handlers.onStatus(phase, detail) — fine-grained, DEV-diagnostic-only
    phase reporting; see ever-leaf-personalizer.js's DEV CART COMMIT panel.
    Never affects behavior.
  */
  function finalizeUpload(file, handlers, scopeEl) {
    handlers = handlers || {};
    var onReady = handlers.onReady || function () {};
    var onError = handlers.onError || function () {};
    var onStatus = handlers.onStatus || function () {};
    var scope = scopeEl || document;

    sanitizeStaleState(scope, onStatus);

    var pond = resolvePondInstance();
    onStatus(pond ? 'pond-found' : 'pond-not-found');
    if (!pond || typeof pond.addFile !== 'function') {
      onError('Upload widget is not available right now.');
      return function () {};
    }

    var config = inspectPondConfig(pond);
    onStatus(
      'pond-config',
      'instantUpload=' +
        config.instantUpload +
        ' allowProcess=' +
        config.allowProcess +
        ' maxParallelUploads=' +
        config.maxParallelUploads +
        ' hasServerProcess=' +
        config.hasServerProcess
    );
    if (config.allowProcess === false || !config.hasServerProcess) {
      // Section 6 — report clearly: this would indicate the wrong FilePond
      // instance, or an app-controlled workflow that cannot be started this
      // way. Not fatal by itself (still attempt processFile below — some
      // Pond configurations report these getters unreliably) but surfaced
      // prominently for diagnosis.
      onStatus('pond-config-suspect', 'allowProcess=' + config.allowProcess + ' hasServerProcess=' + config.hasServerProcess);
    }
    if (typeof pond.status === 'number') {
      onStatus('pond-status', pond.status);
    }

    var settled = false;
    var stageACleanup = null;

    function settleReady(result) {
      if (settled) return;
      settled = true;
      cleanup();
      onReady(result);
    }
    function settleError(message) {
      if (settled) return;
      settled = true;
      cleanup();
      onError(message || 'Upload failed');
    }

    function cleanup() {
      if (stageACleanup) stageACleanup();
    }

    /*
      Correction Pass 7, Section 3/5: once Stage A reports
      PROCESSING_COMPLETE, `item.serverId` IS the authoritative hosted URL.
      Validates it's a non-empty string and resolves — creating NO public
      `properties[Upload*]` input ourselves (that's what caused the Pass 6
      duplicate: Mini's own submit-time hook already adds one). The caller
      is responsible for writing serverId into the PRIVATE
      `_ever_leaf_upload_url` property and for the Section 3 diagnostic scan
      of public Upload inputs/FormData right before the approved resubmit.
    */
    function onStageAComplete(item) {
      if (settled) return;
      var hostedUrl = item.serverId;
      onStatus('serverid-check', hostedUrl);
      var valid = typeof hostedUrl === 'string' && hostedUrl.trim().length > 0;
      if (!valid) {
        settleError('FilePond finished processing, but no valid hosted URL (item.serverId) was returned.');
        return;
      }
      onStatus('serverid-valid', hostedUrl);

      settleReady({
        hostedUrl: hostedUrl,
        itemId: item.id,
        filename: item.filename
      });
    }

    onStatus('addfile-start');
    try {
      var addFileResult = pond.addFile(file);
      if (!addFileResult || typeof addFileResult.then !== 'function') {
        onStatus('addfile-error', 'addFile did not return a Promise — cannot drive Stage A reliably');
        settleError('Upload widget returned an unexpected response.');
        return cleanup;
      }
      addFileResult
        .then(function (item) {
          if (settled || !item) return;
          onStatus('addfile-resolved', item.status);
          stageACleanup = driveFileProcessing(
            pond,
            item,
            onStatus,
            function () {
              onStageAComplete(item);
            },
            function onStageAError(message) {
              settleError(message);
            }
          );
        })
        .catch(function (err) {
          onStatus('addfile-error', (err && err.message) || 'addFile rejected');
          settleError('Could not hand the photo to the upload widget.');
        });
    } catch (e) {
      settleError('Could not hand the photo to the upload widget');
    }

    return cleanup;
  }

  /*
    Confirms exactly one CURRENT hosted-image property exists (mandatory:
    abort if 0, abort if >1). Only counts POPULATED inputs (an empty leftover
    input is not "a hosted image property").
  */
  function countHostedImageProperties(scopeEl) {
    return findPopulatedUploadProperties(scopeEl || document).length;
  }

  window.EverLeafMiniUploadAdapter = {
    finalizeUpload: finalizeUpload,
    sanitizeStaleState: sanitizeStaleState,
    countHostedImageProperties: countHostedImageProperties,
    createOwnUploadProperty: createOwnUploadProperty, // Section 8 fallback only — unused by default, see header comment
    UPLOAD_PROPERTY_SELECTOR: UPLOAD_PROPERTY_SELECTOR,
    UPLOAD_PROPERTY_NAME: UPLOAD_PROPERTY_NAME,
    OWNED_PROPERTY_MARKER: OWNED_PROPERTY_MARKER,
    fileStatusName: fileStatusName,
    pondStatusName: pondStatusName
  };
})();
